import { FormattedMessage, useIntl } from 'react-intl';
import Modal from '@/shared/components/Modal';
import ErrorAlert from '@/shared/components/ErrorAlert';
import ModalButton from '@/shared/components/ModalButton';
import { Schedule } from '@/features/accounts/types';
import { type SchedulePreferencesPayload } from '@/features/accounts/api';
import { useActivationFlow } from '../hooks/useActivationFlow';
import { type ActivationIntent } from '../hooks/useScheduleActivation';
import ActivationSummary from './ActivationModal/ActivationSummary';
import ActivationPayment from './ActivationModal/ActivationPayment';

interface Props {
  schedule: Schedule | null;
  countryCode: string;
  preferences?: SchedulePreferencesPayload;
  intent: ActivationIntent;
  onClose: () => void;
  onBack?: () => void;
  onConfirm: (scheduleId: number) => Promise<unknown>;
  onActivated: () => void;
}

export default function ActivationModal({
  schedule,
  countryCode,
  preferences,
  intent,
  onClose,
  onBack,
  onConfirm,
  onActivated,
}: Props) {
  const intl = useIntl();
  const {
    isSubmitting,
    activationError,
    costCents,
    currency,
    isFunded,
    isPaymentFormReady,
    panelRef,
    handleConfirm,
    handlePaymentFormReady,
    handlePaymentConfirmed,
    handlePaymentError,
  } = useActivationFlow({ schedule, intent, onConfirm, onActivated });

  if (!schedule) return null;

  return (
    <Modal
      isOpen={!!schedule}
      onClose={onClose}
      title={intl.formatMessage({ id: 'schedule.activate.title' })}
      eyebrow={intl.formatMessage({ id: 'schedule.activate.eyebrow' })}
      footer={
        <>
          {onBack ? (
            <ModalButton type="button" onClick={onBack} variant="secondary">
              <FormattedMessage id="schedule.activate.back" />
            </ModalButton>
          ) : (
            <ModalButton type="button" onClick={onClose} variant="secondary">
              <FormattedMessage id="accounts.config.close" />
            </ModalButton>
          )}
          <ModalButton
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || (!isFunded && (costCents == null || !isPaymentFormReady))}
          >
            {isSubmitting ? (
              <FormattedMessage id="schedule.activate.confirming" />
            ) : (
              <FormattedMessage id="schedule.activate.confirm" />
            )}
          </ModalButton>
        </>
      }
    >
      <div className="space-y-3">
        {activationError && <ErrorAlert message={activationError} />}

        <ActivationSummary
          preferences={preferences}
          schedule={schedule}
          countryCode={countryCode}
          showBillingSummary={!isFunded}
          costCents={costCents}
          currency={currency}
        />

        <ActivationPayment
          ref={panelRef}
          scheduleId={schedule.id.toString()}
          isFunded={isFunded}
          onReady={handlePaymentFormReady}
          onPaymentConfirmed={handlePaymentConfirmed}
          onError={handlePaymentError}
        />
      </div>
    </Modal>
  );
}
